import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend,
} from 'chart.js';
import { useFeather } from '../shell/useFeather';
import { useTabParam } from '../shell/useTabParam';
import {
  AffilmasterApiError,
  getKlientiPrehledList,
  getKlientiPrehledTimeseries,
  getKlientiPrehledDashboard,
  type KlientPrehledClient,
  type KlientPrehledMetric,
  type KlientPrehledTimeseries,
  type KlientDashboard,
} from '../api/affilmaster';
import { klientSlug } from './AffilmasterKlientiPage';
import './affilmaster-dashboard.css';
import './affilmaster-detail.css';
import './affilmaster-klienti-prehledy.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

type Market = 'CZ' | 'SK';
type PrehledTab = 'dashboard' | 'mesicni' | 'hlavni';
const PREHLED_TABS: readonly PrehledTab[] = ['dashboard', 'mesicni', 'hlavni'];

const MONTH_CS = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];
const MONTH_SHORT = ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'];
const ymLabel = (ym: string) => { const [y, m] = ym.split('-'); return `${MONTH_CS[Number(m) - 1] ?? m} ${y ?? ''}`; };
const ymShort = (ym: string) => { const [y, m] = ym.split('-'); return `${MONTH_SHORT[Number(m) - 1] ?? m} ${(y ?? '').slice(2)}`; };

const METRIC_LABELS: Record<KlientPrehledMetric, string> = {
  clicks: 'Prokliky',
  registrations: 'Registrace',
  ftd_count: 'FTD',
  ngr_volume: 'NGR',
  commission: 'Odměny celkem',
};
const METRIC_KIND: Record<KlientPrehledMetric, 'count' | 'money'> = {
  clicks: 'count', registrations: 'count', ftd_count: 'count', ngr_volume: 'money', commission: 'money',
};
const METRICS_ORDER: KlientPrehledMetric[] = ['clicks', 'registrations', 'ftd_count', 'ngr_volume', 'commission'];
const CLIENT_COLOR_TOKENS = Array.from({ length: 13 }, (_, index) => `--am-client-color-${index + 1}`);
const CLIENT_COLOR_FALLBACKS = [
  '#6544a5', '#087f3e', '#2563c9', '#d97706', '#c4476f', '#087f84',
  '#aa7414', '#4f55bd', '#c2413b', '#2086b5', '#8755b9', '#66727b', '#222527',
];
const CLIENT_COLOR_INDEX: Record<string, number> = {
  '69 Games': 0,
  Allwyn: 1,
  Grandwin: 2,
  Luckybet: 3,
  'Magic Planet': 12,
  MerkurXtip: 5,
  'Star Casino': 6,
  Tokyo: 8,
  Eurogold: 0,
  'Game World': 1,
  Go4Games: 2,
  'Rebuy Stars': 3,
};

function getClientColor(client: { id: number; name: string }, palette: string[]): string {
  const colorIndex = CLIENT_COLOR_INDEX[client.name] ?? Math.abs(client.id) % palette.length;
  return palette[colorIndex] ?? CLIENT_COLOR_FALLBACKS[colorIndex] ?? CLIENT_COLOR_FALLBACKS[0]!;
}

function isAllwyn(name: string): boolean {
  return /allwyn/i.test(name);
}

// Klienti, od kterých nedostáváme FTD - v přehledu se místo nuly ukáže -n/a-,
// ať se nezaměňuje "nula konverzí" za "nemáme data".
function bezFtd(name: string): boolean {
  return /betano|synot/i.test(name);
}

function fmt(v: number, kind: 'count' | 'money', currency: string) {
  if (!Number.isFinite(v)) return '-';
  if (kind === 'money') {
    const nf = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 });
    return currency === 'EUR' ? `${nf.format(v)} €` : `${nf.format(v)} Kč`;
  }
  return new Intl.NumberFormat('cs-CZ').format(Math.round(v));
}

function fmtAxis(v: number, kind: 'count' | 'money', currency: string) {
  const abs = Math.abs(v);
  const number = abs >= 1_000_000
    ? `${new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 1 }).format(v / 1_000_000)} mil.`
    : abs >= 1_000
      ? `${new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(v / 1_000)} tis.`
      : new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(v);
  if (kind !== 'money') return number;
  return currency === 'EUR' ? `${number} €` : `${number} Kč`;
}

export function AffilmasterKlientiPrehledyPage({ onOpenKlient }: { onOpenKlient?: (slug: string) => void } = {}) {
  const pageRef = useRef<HTMLElement>(null);
  const [tab, setTab] = useTabParam<PrehledTab>('tab', PREHLED_TABS, 'dashboard');
  const [market, setMarket] = useState<Market>('CZ');
  const [clients, setClients] = useState<KlientPrehledClient[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [metricA, setMetricA] = useState<KlientPrehledMetric>('ftd_count');
  const [metricB, setMetricB] = useState<KlientPrehledMetric>('ngr_volume');
  const [ym, setYm] = useState<'all' | string>('all');
  const [ts, setTs] = useState<KlientPrehledTimeseries | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [clientPalette, setClientPalette] = useState(CLIENT_COLOR_FALLBACKS);

  useLayoutEffect(() => {
    if (!pageRef.current) return;
    const style = getComputedStyle(pageRef.current);
    const next = CLIENT_COLOR_TOKENS.map((token, index) => style.getPropertyValue(token).trim() || CLIENT_COLOR_FALLBACKS[index]!);
    setClientPalette(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setErr(null);
    getKlientiPrehledList(market)
      .then((rows) => {
        if (cancelled) return;
        setClients(rows);
        setSelectedIds(rows.map((r) => r.id));
      })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení klientů selhalo'); });
    return () => { cancelled = true; };
  }, [market]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedIds.length) { setTs({ currency: market === 'CZ' ? 'CZK' : 'EUR', months: [], series: [] }); return; }
    setBusy(true); setErr(null);
    const chartMetrics = metricA === metricB ? [metricA] : [metricA, metricB];
    const dashMetrics: KlientPrehledMetric[] = ['registrations', 'ftd_count', 'ngr_volume'];
    const metrics = Array.from(new Set([...chartMetrics, ...dashMetrics])) as KlientPrehledMetric[];
    getKlientiPrehledTimeseries({ market, partnerIds: selectedIds, metrics, ym })
      .then((d) => { if (!cancelled) setTs(d); })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení grafu selhalo'); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [market, selectedIds, metricA, metricB, ym]);

  useFeather([tab, market, clients, selectedIds, metricA, metricB, ym, ts, busy, err]);

  const months = ts?.months ?? [];
  const currency = ts?.currency ?? (market === 'CZ' ? 'CZK' : 'EUR');
  const monthOpts = useMemo(() => {
    const all: string[] = [];
    if (months.length) all.push(...months);
    else {
      const now = new Date();
      for (let i = 0; i < 24; i++) { all.push(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`); now.setMonth(now.getMonth() - 1); }
    }
    return Array.from(new Set(all)).sort().reverse();
  }, [months]);

  const clientColors = useMemo(
    () => new Map(clients.map((client) => [client.id, getClientColor(client, clientPalette)])),
    [clients, clientPalette],
  );
  const selectedClients = useMemo(() => clients.filter((client) => selectedIds.includes(client.id)), [clients, selectedIds]);

  const chartData = useMemo(() => {
    const labels = months.map(ymShort);
    const datasets: Array<Record<string, unknown>> = [];
    const wanted = new Set<string>([metricA, metricB]);
    ts?.series.filter((s) => wanted.has(s.metric)).forEach((s) => {
      const color = clientColors.get(s.partnerId) ?? clientPalette[0]!;
      const secondary = metricA !== metricB && s.metric === metricB;
      datasets.push({
        label: `${s.name} · ${METRIC_LABELS[s.metric as KlientPrehledMetric]}`,
        data: s.values,
        borderColor: color,
        backgroundColor: color,
        yAxisID: secondary ? 'yB' : 'yA',
        tension: 0.36,
        borderWidth: secondary ? 1.8 : 2.35,
        borderDash: secondary ? [7, 5] : [],
        borderCapStyle: 'round',
        borderJoinStyle: 'round',
        pointRadius: months.length <= 2 ? 3.5 : 0,
        pointHoverRadius: 4.5,
        pointHoverBorderWidth: 2,
        pointBackgroundColor: '#fff',
        pointBorderColor: color,
        fill: false,
      });
    });
    return { labels, datasets, twoAxes: metricA !== metricB };
  }, [ts, months, metricA, metricB, clientColors, clientPalette]);

  const chartOptions = useMemo(() => {
    const kindA = METRIC_KIND[metricA];
    const kindB = METRIC_KIND[metricB];
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: 'index' as const },
      animation: { duration: 320 },
      layout: { padding: { top: 8, right: 6 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#202326',
          titleColor: '#fff',
          bodyColor: '#f7f8f8',
          padding: 12,
          boxPadding: 5,
          cornerRadius: 10,
          usePointStyle: true,
          callbacks: {
            label: (ctx: { dataset: { label?: string; yAxisID?: string }; parsed: { y: number } }) => {
              const isA = ctx.dataset.yAxisID === 'yA';
              const kind = isA ? kindA : kindB;
              return ` ${ctx.dataset.label}: ${fmt(ctx.parsed.y, kind, currency)}`;
            },
          },
        },
      },
      scales: {
        x: {
          border: { display: false },
          grid: { display: false },
          ticks: { color: '#7a838b', font: { size: 10 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
        },
        yA: {
          type: 'linear' as const,
          position: 'left' as const,
          beginAtZero: true,
          border: { display: false },
          grid: { color: '#edf0f1' },
          ticks: { color: '#7a838b', font: { size: 10 }, padding: 8, callback: (v: number | string) => fmtAxis(Number(v), kindA, currency) },
        },
        ...(chartData.twoAxes ? {
          yB: {
            type: 'linear' as const,
            position: 'right' as const,
            beginAtZero: true,
            border: { display: false },
            grid: { drawOnChartArea: false },
            ticks: { color: '#7a838b', font: { size: 10 }, padding: 8, callback: (v: number | string) => fmtAxis(Number(v), kindB, currency) },
          },
        } : {}),
      },
    };
  }, [metricA, metricB, currency, chartData.twoAxes]);

  const toggleClient = (id: number) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const selectAll = () => setSelectedIds(clients.map((c) => c.id));
  const selectNone = () => setSelectedIds([]);
  const chartDescription = metricA === metricB
    ? `${METRIC_LABELS[metricA]} · jedna společná osa`
    : `${METRIC_LABELS[metricA]} vlevo · ${METRIC_LABELS[metricB]} vpravo`;

  return (
    <section ref={pageRef} className="affms-dash am-client-overview">
      <header className="prototype-page-head am-client-overview-head">
        <div>
          <div className="prototype-eyebrow">AFFILMASTER</div>
          <h1>Klienti</h1>
          <p>Srovnání výkonu klientů napříč metrikami a obdobím</p>
        </div>
      </header>

      {err && (
        <div className="prototype-notice prototype-notice-error" role="alert">
          <i data-feather="alert-circle" />
          <div><strong>Data se nepodařilo načíst</strong><span>{err}</span></div>
        </div>
      )}

      <div className="prototype-overview-toolbar am-client-overview-toolbar">
        <div className="prototype-overview-tabs" role="tablist">
          <button type="button" className={tab === 'dashboard' ? 'active' : ''} role="tab" aria-selected={tab === 'dashboard'} onClick={() => setTab('dashboard')}>Dashboard</button>
          <button type="button" className={tab === 'mesicni' ? 'active' : ''} role="tab" aria-selected={tab === 'mesicni'} onClick={() => setTab('mesicni')}>Měsíční</button>
          <button type="button" className={tab === 'hlavni' ? 'active' : ''} role="tab" aria-selected={tab === 'hlavni'} onClick={() => setTab('hlavni')}>Hlavní</button>
        </div>
      </div>

      {tab === 'dashboard' && <KlientDashboardTab palette={clientPalette} onOpenKlient={onOpenKlient} />}

      {tab === 'mesicni' && <KlientMesicniTab palette={clientPalette} onOpenKlient={onOpenKlient} />}

      {tab === 'hlavni' && (
        <>
          <section className="prototype-panel am-client-controls" aria-label="Nastavení přehledu">
            <div className="am-client-controls-grid">
              <div className="am-client-control-field">
                <span className="am-client-control-label">Trh</span>
                <div className="am-client-market-switch" role="group" aria-label="Trh">
                  <button type="button" className={market === 'CZ' ? 'active' : ''} aria-pressed={market === 'CZ'} onClick={() => setMarket('CZ')}><span>CZ</span>Česko</button>
                  <button type="button" className={market === 'SK' ? 'active' : ''} aria-pressed={market === 'SK'} onClick={() => setMarket('SK')}><span>SK</span>Slovensko</button>
                </div>
              </div>

              <label className="am-client-control-field">
                <span className="am-client-control-label">Období</span>
                <span className="am-client-select-shell am-client-period-select">
                  <i data-feather="calendar" />
                  <select value={ym} aria-label="Období" onChange={(e) => setYm(e.target.value as 'all' | string)}>
                    <option value="all">Celé období</option>
                    {monthOpts.map((o) => <option key={o} value={o}>{ymLabel(o)}</option>)}
                  </select>
                </span>
              </label>

              <MetricControl axis="A" side="Levá osa" line="Plná čára" value={metricA} onChange={setMetricA} />
              <MetricControl axis="B" side={metricA === metricB ? 'Společná osa' : 'Pravá osa'} line="Čárkovaná" value={metricB} onChange={setMetricB} dashed />
            </div>

            <div className="am-client-picker">
              <div className="am-client-picker-head">
                <div><span className="am-client-control-label">Klienti</span><strong>{selectedIds.length} z {clients.length} vybráno</strong></div>
                <div className="am-client-picker-actions">
                  <button type="button" disabled={selectedIds.length === clients.length} onClick={selectAll}>Vybrat vše</button>
                  <span />
                  <button type="button" disabled={selectedIds.length === 0} onClick={selectNone}>Zrušit výběr</button>
                </div>
              </div>
              <div className="am-client-chip-list">
                {clients.map((client) => {
                  const selected = selectedIds.includes(client.id);
                  const chipStyle = { '--am-chip-color': clientColors.get(client.id) } as CSSProperties;
                  return (
                    <button key={client.id} type="button" className={`am-client-chip${selected ? ' active' : ''}`} style={chipStyle} aria-pressed={selected} onClick={() => toggleClient(client.id)}>
                      <span className="am-client-chip-dot" />
                      <span>{client.name}</span>
                      <span className="am-client-chip-check" aria-hidden="true">{selected ? '✓' : '+'}</span>
                    </button>
                  );
                })}
                {clients.length === 0 && <div className="am-client-picker-empty">Žádní klienti na trhu {market}.</div>}
              </div>
            </div>
          </section>

          <section className="prototype-panel am-client-chart-panel">
            <header className="am-client-chart-head">
              <div>
                <span className="am-client-chart-eyebrow">VÝVOJ V ČASE</span>
                <h2>Výkon klientů</h2>
                <p>{chartDescription}</p>
              </div>
              <div className="am-client-chart-meta">
                <span>{currency}</span>
                <span>{months.length} období</span>
              </div>
            </header>

            <div className="am-client-chart-key" aria-label="Legenda grafu">
              <div className="am-client-axis-key">
                <span><i className="solid" /><strong>{METRIC_LABELS[metricA]}</strong><small>levá osa</small></span>
                {metricA !== metricB && <span><i className="dashed" /><strong>{METRIC_LABELS[metricB]}</strong><small>pravá osa</small></span>}
              </div>
              {selectedClients.length > 0 && (
                <div className="am-client-legend-clients">
                  {selectedClients.map((client) => (
                    <span key={client.id} style={{ '--am-legend-color': clientColors.get(client.id) } as CSSProperties}><i />{client.name}</span>
                  ))}
                </div>
              )}
            </div>

            <div className="am-client-chart-canvas">
              {busy && <div className="prototype-page-loading am-client-chart-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>}
              {!busy && selectedIds.length === 0 && (
                <ChartEmpty icon="users" title="Vyberte alespoň jednoho klienta" text="Graf se zobrazí hned po výběru klienta výše." />
              )}
              {!busy && selectedIds.length > 0 && months.length === 0 && (
                <ChartEmpty icon="bar-chart-2" title="Pro tento výběr nejsou data" text="Zkuste jiné období, metriku nebo klienta." />
              )}
              {!busy && months.length > 0 && <Line data={chartData as never} options={chartOptions as never} />}
            </div>
          </section>
        </>
      )}
    </section>
  );
}

function MetricControl({ axis, side, line, value, onChange, dashed = false }: {
  axis: 'A' | 'B';
  side: string;
  line: string;
  value: KlientPrehledMetric;
  onChange: (metric: KlientPrehledMetric) => void;
  dashed?: boolean;
}) {
  return (
    <label className="am-client-control-field am-client-metric-control">
      <span className="am-client-control-label"><b>{axis}</b> Metrika <small>{side}</small></span>
      <span className="am-client-select-shell">
        <i className={`am-client-line-sample${dashed ? ' dashed' : ''}`} />
        <select value={value} aria-label={`Metrika ${axis}`} onChange={(event) => onChange(event.target.value as KlientPrehledMetric)}>
          {METRICS_ORDER.map((metric) => <option key={metric} value={metric}>{METRIC_LABELS[metric]}</option>)}
        </select>
        <small>{line}</small>
      </span>
    </label>
  );
}

function KlientDashboardTab({ palette, onOpenKlient }: { palette: string[]; onOpenKlient?: (slug: string) => void }) {
  const [data, setData] = useState<KlientDashboard | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setBusy(true); setErr(null);
    getKlientiPrehledDashboard()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení dashboardu selhalo'); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, []);

  useFeather([data, busy, err]);

  const ordered = useMemo(() => {
    if (!data) return [];
    return [...data.clients].sort((a, b) => b.deposits_volume - a.deposits_volume);
  }, [data]);

  const marketGroups = useMemo(() => ([
    { code: 'CZ' as const, name: 'Český trh', clients: ordered.filter((client) => client.market === 'CZ') },
    { code: 'SK' as const, name: 'Slovenský trh', clients: ordered.filter((client) => client.market === 'SK') },
  ]), [ordered]);

  if (err) {
    return (
      <div className="prototype-notice prototype-notice-error" role="alert">
        <i data-feather="alert-circle" />
        <div><strong>Data se nepodařilo načíst</strong><span>{err}</span></div>
      </div>
    );
  }

  if (busy && !data) {
    return <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /></div>;
  }

  return (
    <>
      {ordered.length === 0 ? (
        <div className="prototype-panel am-client-dashboard-empty">Žádní aktivní klienti.</div>
      ) : (
        <section className="am-client-market-grid" aria-label="Výkon klientů podle trhu">
          {marketGroups.map((group) => (
            <article key={group.code} className="prototype-panel am-client-market-panel">
              <header className="am-client-market-head">
                <span className={`am-client-market-code ${group.code.toLowerCase()}`}>{group.code}</span>
                <div>
                  <h3>{group.name}</h3>
                  <p>{group.clients.length} {group.clients.length === 1 ? 'aktivní klient' : group.clients.length < 5 ? 'aktivní klienti' : 'aktivních klientů'}</p>
                </div>
              </header>

              {group.clients.length === 0 ? (
                <div className="am-client-market-empty">V tomto měsíci bez aktivních klientů.</div>
              ) : (
                <div className="am-client-market-table-wrap">
                  <table className="am-client-market-table">
                    <thead>
                      <tr>
                        <th>Klient</th>
                        <th>Vklady</th>
                        <th>NGR</th>
                        <th>Registrace</th>
                        <th>FTD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.clients.map((client) => {
                        const color = getClientColor(client, palette);
                        const style = { '--am-client-row-color': color } as CSSProperties;
                        return (
                          <tr key={client.id} style={style}>
                            <td className="am-client-data-name">
                              <div className="am-client-data-name-inner">
                                <span className="am-client-color-dot" aria-hidden="true" />
                                {onOpenKlient ? (
                                  <button
                                    type="button"
                                    className="am-client-data-name-copy am-client-name-link"
                                    onClick={() => onOpenKlient(klientSlug(client.name))}
                                    title={`Otevřít kartu klienta ${client.name}`}
                                  >
                                    <strong>{client.name}</strong><small>{client.currency}</small>
                                  </button>
                                ) : (
                                  <span className="am-client-data-name-copy"><strong>{client.name}</strong><small>{client.currency}</small></span>
                                )}
                              </div>
                            </td>
                            <td className="am-client-data-primary" data-label="Vklady">
                              {fmt(client.deposits_volume, 'money', client.currency)}
                              {client.altCurrency && client.altDeposits != null && (
                                <small className="am-client-alt-currency">{fmt(client.altDeposits, 'money', client.altCurrency)}</small>
                              )}
                            </td>
                            <td data-label="NGR">
                              {isAllwyn(client.name)
                                ? <span className="is-muted">-n/a-</span>
                                : (
                                  <>
                                    <span className={client.ngr_volume < 0 ? 'is-negative' : ''}>{fmt(client.ngr_volume, 'money', client.currency)}</span>
                                    {client.altCurrency && client.altNgr != null && (
                                      <small className="am-client-alt-currency">{fmt(client.altNgr, 'money', client.altCurrency)}</small>
                                    )}
                                  </>
                                )}
                            </td>
                            <td data-label="Registrace">{fmt(client.registrations, 'count', client.currency)}</td>
                            <td data-label="FTD">
                              {bezFtd(client.name)
                                ? <span className="is-muted">-n/a-</span>
                                : fmt(client.ftd_count, 'count', client.currency)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </article>
          ))}
        </section>
      )}
    </>
  );
}

function lastMonths(count: number): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < count; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

function KlientMesicniTab({ palette, onOpenKlient }: { palette: string[]; onOpenKlient?: (slug: string) => void }) {
  const monthOpts = useMemo(() => lastMonths(24), []);
  const [market, setMarket] = useTabParam<Market>('trh', ['CZ', 'SK'], 'CZ', false);
  const [ym, setYm] = useTabParam<string>('mesic', monthOpts, monthOpts[0]!, false);
  const [data, setData] = useState<KlientDashboard | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setBusy(true); setErr(null);
    getKlientiPrehledDashboard(ym)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení měsíčního přehledu selhalo'); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [ym]);

  useFeather([data, busy, err, market, ym]);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.clients.filter((c) => c.market === market).sort((a, b) => b.deposits_volume - a.deposits_volume);
  }, [data, market]);
  const currency = market === 'CZ' ? 'CZK' : 'EUR';
  const totals = useMemo(() => rows.reduce((acc, c) => ({
    deposits: acc.deposits + c.deposits_volume,
    ngr: acc.ngr + (isAllwyn(c.name) ? 0 : c.ngr_volume),
    registrations: acc.registrations + c.registrations,
    ftd: acc.ftd + (bezFtd(c.name) ? 0 : c.ftd_count),
  }), { deposits: 0, ngr: 0, registrations: 0, ftd: 0 }), [rows]);

  return (
    <>
      <section className="prototype-panel am-client-controls" aria-label="Nastavení měsíčního přehledu">
        <div className="am-client-controls-grid am-client-controls-grid-compact">
          <div className="am-client-control-field">
            <span className="am-client-control-label">Trh</span>
            <div className="am-client-market-switch" role="group" aria-label="Trh">
              <button type="button" className={market === 'CZ' ? 'active' : ''} aria-pressed={market === 'CZ'} onClick={() => setMarket('CZ')}><span>CZ</span>Česko</button>
              <button type="button" className={market === 'SK' ? 'active' : ''} aria-pressed={market === 'SK'} onClick={() => setMarket('SK')}><span>SK</span>Slovensko</button>
            </div>
          </div>
          <label className="am-client-control-field">
            <span className="am-client-control-label">Měsíc</span>
            <span className="am-client-select-shell am-client-period-select">
              <i data-feather="calendar" />
              <select value={ym} aria-label="Měsíc" onChange={(e) => setYm(e.target.value)}>
                {monthOpts.map((o) => <option key={o} value={o}>{ymLabel(o)}</option>)}
              </select>
            </span>
          </label>
        </div>
      </section>

      {err && (
        <div className="prototype-notice prototype-notice-error" role="alert">
          <i data-feather="alert-circle" />
          <div><strong>Data se nepodařilo načíst</strong><span>{err}</span></div>
        </div>
      )}

      <article className="prototype-panel am-client-market-panel am-client-mesicni-panel">
        <header className="am-client-market-head">
          <span className={`am-client-market-code ${market.toLowerCase()}`}>{market}</span>
          <div>
            <h3>{market === 'CZ' ? 'Český trh' : 'Slovenský trh'} · {ymLabel(ym)}</h3>
            <p>{rows.length} {rows.length === 1 ? 'aktivní klient' : rows.length < 5 ? 'aktivní klienti' : 'aktivních klientů'} · {currency}</p>
          </div>
        </header>
        {busy && !data ? (
          <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /></div>
        ) : rows.length === 0 ? (
          <div className="am-client-market-empty">V tomto měsíci bez aktivních klientů.</div>
        ) : (
          <div className="am-client-market-table-wrap">
            <table className="am-client-market-table am-client-mesicni-table">
              <thead>
                <tr>
                  <th>Klient</th>
                  <th>Vklady</th>
                  <th>NGR</th>
                  <th>Registrace</th>
                  <th>FTD</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((client) => {
                  const style = { '--am-client-row-color': getClientColor(client, palette) } as CSSProperties;
                  return (
                    <tr key={client.id} style={style}>
                      <td className="am-client-data-name">
                        <div className="am-client-data-name-inner">
                          <span className="am-client-color-dot" aria-hidden="true" />
                          {onOpenKlient ? (
                            <button type="button" className="am-client-data-name-copy am-client-name-link" onClick={() => onOpenKlient(klientSlug(client.name))} title={`Otevřít kartu klienta ${client.name}`}>
                              <strong>{client.name}</strong><small>{client.currency}</small>
                            </button>
                          ) : (
                            <span className="am-client-data-name-copy"><strong>{client.name}</strong><small>{client.currency}</small></span>
                          )}
                        </div>
                      </td>
                      <td className="am-client-data-primary" data-label="Vklady">
                        {fmt(client.deposits_volume, 'money', client.currency)}
                        {client.altCurrency && client.altDeposits != null && <small className="am-client-alt-currency">{fmt(client.altDeposits, 'money', client.altCurrency)}</small>}
                      </td>
                      <td data-label="NGR">
                        {isAllwyn(client.name) ? <span className="is-muted">-n/a-</span> : (
                          <>
                            <span className={client.ngr_volume < 0 ? 'is-negative' : ''}>{fmt(client.ngr_volume, 'money', client.currency)}</span>
                            {client.altCurrency && client.altNgr != null && <small className="am-client-alt-currency">{fmt(client.altNgr, 'money', client.altCurrency)}</small>}
                          </>
                        )}
                      </td>
                      <td data-label="Registrace">{fmt(client.registrations, 'count', client.currency)}</td>
                      <td data-label="FTD">{bezFtd(client.name) ? <span className="is-muted">-n/a-</span> : fmt(client.ftd_count, 'count', client.currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="am-client-data-name"><span className="am-client-data-name-copy"><strong>Celkem</strong><small>{currency}</small></span></td>
                  <td className="am-client-data-primary" data-label="Vklady">{fmt(totals.deposits, 'money', currency)}</td>
                  <td data-label="NGR"><span className={totals.ngr < 0 ? 'is-negative' : ''}>{fmt(totals.ngr, 'money', currency)}</span></td>
                  <td data-label="Registrace">{fmt(totals.registrations, 'count', currency)}</td>
                  <td data-label="FTD">{fmt(totals.ftd, 'count', currency)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </article>
    </>
  );
}

function ChartEmpty({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div className="am-client-chart-empty">
      <span><i data-feather={icon} /></span>
      <strong>{title}</strong>
      <p>{text}</p>
    </div>
  );
}
